import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ClipboardList, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';
import { logger } from '@/utils/logger';
import { useDarkMode } from '@/hooks/useDarkMode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const OCCASIONS = ['Birthday', 'Christmas', 'Anniversary', 'Thank you', 'Just because'];
const BUDGETS = ['Under $25', '$25 - $50', '$50 - $100', '$100+'];
const VIBES = ['Cozy homebody', 'Adventurer', 'Foodie', 'Tech lover', 'Creative soul', 'Bookworm', 'Wellness', 'Minimalist'];

// The instant gift finder: one line of free text + optional chips → AI picks.
export default function GiftGenieBar({ onResults, onCreateQuestionnaire }) {
  const darkMode = useDarkMode();
  const [text, setText] = useState('');
  const [occasion, setOccasion] = useState('');
  const [budget, setBudget] = useState('');
  const [vibes, setVibes] = useState([]);
  const [showChips, setShowChips] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const focus = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 450);
    };
    window.addEventListener('mementa:focus-genie', focus);
    return () => window.removeEventListener('mementa:focus-genie', focus);
  }, []);

  const submit = async () => {
    const trimmed = text.trim();
    const hasChips = vibes.length > 0 || occasion || budget;
    if (trimmed.length < 3 && !hasChips) {
      toast.info('Type a few words about them, or tap a couple of chips below');
      inputRef.current?.focus();
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/ai-recommendations', {
        quick_text: trimmed,
        occasion,
        budget,
        vibes
      });
      onResults(
        res.data.recommendations,
        res.data.personality_summary || '',
        '',
        res.data.creative_alternatives || [],
        res.data.all_matches || []
      );
    } catch (err) {
      logger.error('Quick gift finder failed:', err);
      if (err?.response?.status === 429) {
        toast.error('Whoa, that was fast, give it a few seconds and try again');
      } else {
        toast.error('Could not fetch gift ideas right now. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto page-rise-3" data-testid="gift-genie-bar">
      <div
        className="rounded-2xl p-1.5 sm:p-2 flex flex-col sm:flex-row gap-2"
        style={{
          background: darkMode.isDark ? 'rgba(31, 45, 56, 0.96)' : 'rgba(255,255,255,0.95)',
          border: darkMode.isDark ? '1px solid rgba(255,255,255,0.12)' : 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
        }}
      >
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !loading) submit(); }}
          maxLength={600}
          placeholder={'e.g. "Mom, turning 60, loves gardening", or just tap chips'}
          className="flex-1 px-4 py-2.5 rounded-xl text-base sm:text-lg focus:outline-none"
          style={{ background: 'transparent', color: darkMode.isDark ? '#FFFAF5' : '#20212B' }}
          data-testid="gift-genie-input"
        />
        <button
          onClick={submit}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm sm:text-base transition-all hover:scale-[1.03] disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
          style={{ background: '#9E6752', color: 'white' }}
          data-testid="gift-genie-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Finding gifts…
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Get gift ideas
            </>
          )}
        </button>
      </div>

      {/* One-tap refinements as compact overlay dropdowns, opening them
          never resizes or reflows the hero (menus portal above the page).
          The "Send them a questionnaire" CTA lives inline so all
          non-instant options share the same row. */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <RefineDropdown
          label="Vibe"
          testid="gift-genie-vibe-dropdown"
          summary={vibes.length ? `Vibe · ${vibes.length}` : 'Vibe'}
          active={vibes.length > 0}
          options={VIBES}
          isSelected={(v) => vibes.includes(v)}
          onToggle={(v) => setVibes(vibes.includes(v) ? vibes.filter((x) => x !== v) : [...vibes, v])}
          multi
        />
        <RefineDropdown
          label="Occasion"
          testid="gift-genie-occasion-dropdown"
          summary={occasion || 'Occasion'}
          active={!!occasion}
          options={OCCASIONS}
          isSelected={(o) => occasion === o}
          onToggle={(o) => setOccasion(occasion === o ? '' : o)}
        />
        <RefineDropdown
          label="Budget"
          testid="gift-genie-budget-dropdown"
          summary={budget || 'Budget'}
          active={!!budget}
          options={BUDGETS}
          isSelected={(b) => budget === b}
          onToggle={(b) => setBudget(budget === b ? '' : b)}
        />
        {onCreateQuestionnaire && (
          <button
            type="button"
            onClick={onCreateQuestionnaire}
            data-testid="hero-create-questionnaire-btn"
            aria-label="Send them a gift questionnaire"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all hover:scale-105"
            style={{
              background: 'rgba(255,255,255,0.16)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.4)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <ClipboardList className="w-3.5 h-3.5" style={{ color: '#FED7A5' }} />
            Send them a questionnaire
          </button>
        )}
      </div>
    </div>
  );
}

// Pill-shaped trigger + portal-rendered menu so the hero never reflows.
function RefineDropdown({ label, testid, summary, active, options, isSelected, onToggle, multi = false }) {
  return (
    // modal={false}: Radix's body scroll-lock would kill position:sticky on
    // the nav while a menu is open (nav vanishes when scrolled). No lock needed.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-label={`Refine by ${label.toLowerCase()}`}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all hover:scale-105"
          style={{
            background: active ? '#FED7A5' : 'rgba(255,255,255,0.16)',
            color: active ? '#20212B' : 'white',
            border: `1px solid ${active ? '#FED7A5' : 'rgba(255,255,255,0.4)'}`,
            backdropFilter: 'blur(4px)'
          }}
        >
          {summary}
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="bottom" sideOffset={6} avoidCollisions={false} className="min-w-[180px] max-h-[260px] overflow-y-auto">
        {options.map((opt) => {
          const selected = isSelected(opt);
          return (
            <DropdownMenuItem
              key={opt}
              data-testid={`${testid}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              onSelect={(e) => {
                if (multi) e.preventDefault();
                onToggle(opt);
              }}
              className="flex items-center justify-between gap-3 cursor-pointer"
            >
              <span>{opt}</span>
              {selected && <Check className="w-4 h-4" style={{ color: '#9E6752' }} />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
